/**
 * Flag that swaps the `active_orders` fetch for the local fixtures.
 *
 * A **runtime** flag, not a build-time one, on purpose. This package ships as
 * TypeScript source (`main` is `src/index.ts`), so host apps compile it with
 * their own bundler — `import.meta.env` would tie us to Vite and, as
 * {@link ../utils/debug.ts} notes, build mode is not a reliable signal here
 * anyway: one production build is pointed at any backend through the `env`
 * prop.
 *
 * Two ways in, both no-ops on `prd`:
 *   - `?mockActiveOrders=<preset>` on the URL — no rebuild, so QA and design
 *     can walk every card state in a deployed host app.
 *   - {@link setActiveOrdersMock} from the host app or a test.
 *
 * The fixtures themselves are loaded by dynamic `import()` in
 * {@link ../api/activeOrdersApi.fetchActiveOrders}, so they are code-split into
 * a chunk a production bundle never requests.
 */

import { normalizeEnvironment } from '../utils/normalizeEnv'

import type { ActiveOrder } from '../api/activeOrdersApi'
import type { EnvironmentWithChina } from '../App'

/** URL param that turns the mock on, e.g. `?mockActiveOrders=returns`. */
export const MOCK_ACTIVE_ORDERS_PARAM = 'mockActiveOrders'

/**
 * Which slice of the fixtures to serve. `all` is the default — every state at
 * once, which is what you usually want when reviewing the cards.
 */
export type ActiveOrdersMockPreset =
  | 'all'
  | 'contract'
  | 'orders'
  | 'customMade'
  | 'alterations'
  | 'returns'
  | 'exchanges'
  | 'moneyDirection'
  | 'splitShipment'
  | 'empty'

const PRESETS: ReadonlySet<string> = new Set<ActiveOrdersMockPreset>([
  'all',
  'contract',
  'orders',
  'customMade',
  'alterations',
  'returns',
  'exchanges',
  'moneyDirection',
  'splitShipment',
  'empty',
])

/** Values that read as "on" without naming a preset. */
const TRUTHY = new Set(['', 'true', '1', 'yes', 'on'])

/**
 * Host/test override. `null` means "not set" — fall through to the URL — which
 * is why this is tri-state rather than a boolean.
 */
let override: ActiveOrdersMockPreset | false | null = null

/**
 * Turn the mock on or off programmatically. Pass a preset to pick a slice,
 * `false` to force the real API even with the URL param present, or `null` to
 * hand control back to the URL.
 */
export function setActiveOrdersMock(
  preset: ActiveOrdersMockPreset | boolean | null,
): void {
  if (preset === true) {
    override = 'all'
  } else if (preset === false) {
    override = false
  } else {
    override = preset
  }
}

function readPresetFromUrl(): ActiveOrdersMockPreset | null {
  if (typeof window === 'undefined') return null

  const raw = new URLSearchParams(window.location.search).get(
    MOCK_ACTIVE_ORDERS_PARAM,
  )
  if (raw === null) return null

  const value = raw.trim().toLowerCase()
  if (TRUTHY.has(value)) return 'all'
  if (value === 'false' || value === '0' || value === 'off') return null

  // Preset names are camelCase in the type but case-insensitive on the URL.
  const match = [...PRESETS].find((preset) => preset.toLowerCase() === value)
  if (match) return match as ActiveOrdersMockPreset

  console.warn(
    `[ActiveOrders] Unknown ${MOCK_ACTIVE_ORDERS_PARAM} preset "${raw}" — serving all fixtures. Known presets: ${[
      ...PRESETS,
    ].join(', ')}`,
  )

  return 'all'
}

/**
 * The preset to serve, or `null` to call the real API.
 *
 * Never returns a preset on production: a real customer must not be shown
 * fabricated order data, whatever the URL says.
 */
export function getActiveOrdersMockPreset(
  env: EnvironmentWithChina,
): ActiveOrdersMockPreset | null {
  if (normalizeEnvironment(env) === 'prd') return null
  if (override !== null) return override === false ? null : override

  return readPresetFromUrl()
}

/** Whether the mock will be served for this environment. */
export function isActiveOrdersMockEnabled(env: EnvironmentWithChina): boolean {
  return getActiveOrdersMockPreset(env) !== null
}

/**
 * Resolve a preset to rows. Kept separate from the flag so the dynamic import
 * in the API module has one thing to await.
 */
export async function loadActiveOrdersMock(
  preset: ActiveOrdersMockPreset,
): Promise<ActiveOrder[]> {
  const fixtures = await import('./activeOrders')

  switch (preset) {
    case 'contract':
      return fixtures.ACTIVE_ORDERS_MOCK
    case 'orders':
      return fixtures.REGULAR_ORDER_MOCKS
    case 'customMade':
      return fixtures.CUSTOM_MADE_MOCKS
    case 'alterations':
      return fixtures.ALTERATION_MOCKS
    case 'returns':
      return fixtures.RETURN_MOCKS
    case 'exchanges':
      return fixtures.EXCHANGE_MOCKS
    case 'moneyDirection':
      return fixtures.MONEY_DIRECTION_MOCKS
    case 'splitShipment':
      return fixtures.SPLIT_SHIPMENT_CARDS
    case 'empty':
      return []
    default:
      return fixtures.ALL_ACTIVE_ORDERS_MOCK
  }
}
